/**
 * NL-014 funnel calculation receipt builder.
 *
 * This is the composition point for the three contract families: NL-010
 * seasonal demand, NL-014 price evidence, and NL-041 package recommendation.
 * A component is included only when the caller supplies it *and* its own
 * contract already accepted it, so the single rule this module enforces is that
 * a blocked, incomplete, or manual-quote upstream can never be recorded as a
 * consumed input of a planning estimate.
 *
 * It is pure: no clock, no I/O, no recalculation, and no state mutation. The
 * assembled receipt is returned only if `validateFunnelCalculationReceipt`
 * accepts it, which keeps the receipt rules in exactly one place.
 */
import type { PriceEvidenceReceiptValidationResult } from "../calculator-contracts/price-evidence-receipt";
import type { PackageRecommendation } from "../packages/package-recommender";
import {
  FUNNEL_CALCULATION_FORMULA_VERSION,
  FUNNEL_CALCULATION_METHOD_VERSION,
  FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION,
  validateFunnelCalculationReceipt,
  type FunnelCalculationComponentReference,
  type FunnelCalculationReceipt,
  type FunnelCalculationReceiptBlocker,
  type FunnelCalculationResultReference,
} from "./calculation-receipt";
import type { SeasonalDemandReceiptValidationResult } from "./seasonality/receipts";
import { DECIMAL_ROUNDING_CONTRACT_VERSION } from "../calculator-contracts/decimal";
import type { FunnelStateVersion } from "./schema";

export const FUNNEL_CALCULATION_RECEIPT_BUILDER_CONTRACT_VERSION =
  "nl014-funnel-calculation-receipt-builder-v1";

/** Refusals the builder decides from upstream state, before a receipt exists. */
export type FunnelCalculationReceiptBuildOnlyBlocker =
  | "seasonal-demand-component-not-ready"
  | "price-evidence-component-not-ready"
  | "package-recommendation-component-requires-manual-quote";

export type FunnelCalculationReceiptBuildBlocker =
  | FunnelCalculationReceiptBuildOnlyBlocker
  | FunnelCalculationReceiptBlocker;

/**
 * Each component pairs the upstream contract's own verdict with a caller-owned
 * hash of the artifact. Omitting a component declares it was not consumed.
 */
export type SeasonalDemandComponentInput = {
  readonly validation: SeasonalDemandReceiptValidationResult;
  readonly receiptHash: string;
};

export type PriceEvidenceComponentInput = {
  readonly validation: PriceEvidenceReceiptValidationResult;
  readonly receiptHash: string;
};

export type PackageRecommendationComponentInput = {
  readonly recommendation: PackageRecommendation;
  readonly receiptHash: string;
};

export type FunnelCalculationReceiptBuildInput = {
  readonly funnelStateVersion: FunnelStateVersion;
  readonly outputScale: number;
  /** Defaults to `FUNNEL_CALCULATION_METHOD_VERSION` when the caller omits it. */
  readonly calculatorVersion?: string;
  /** Defaults to `FUNNEL_CALCULATION_FORMULA_VERSION` when the caller omits it. */
  readonly formulaVersion?: string;
  /** Canonical UTC timestamp (`YYYY-MM-DDTHH:mm:ssZ`), supplied by the caller. */
  readonly calculationTimestamp: string;
  /** A caller-supplied UTC calendar date; this module never reads the clock. */
  readonly asOfDate: string;
  /** Canonical UTC calendar date (`YYYY-MM-DD`) the evidence is anchored to. */
  readonly evidenceDate: string;
  /** Caller-owned hashes of the canonical estimate inputs and outputs. */
  readonly inputsHash: string;
  readonly outputsHash: string;
  /** Privacy-safe reference to the computed result (`resultHash` + `unit`). */
  readonly result: FunnelCalculationResultReference;
  readonly seasonalDemand?: SeasonalDemandComponentInput;
  readonly priceEvidence?: PriceEvidenceComponentInput;
  readonly packageRecommendation?: PackageRecommendationComponentInput;
};

export type FunnelCalculationReceiptBuildResult =
  | {
      readonly contractVersion: typeof FUNNEL_CALCULATION_RECEIPT_BUILDER_CONTRACT_VERSION;
      readonly status: "built";
      readonly receipt: FunnelCalculationReceipt;
      readonly blockers: readonly [];
    }
  | {
      readonly contractVersion: typeof FUNNEL_CALCULATION_RECEIPT_BUILDER_CONTRACT_VERSION;
      readonly status: "blocked";
      readonly receipt: null;
      readonly blockers: readonly FunnelCalculationReceiptBuildBlocker[];
    };

const NOT_USED: FunnelCalculationComponentReference = Object.freeze({
  status: "not-used",
  contractVersion: null,
  receiptHash: null,
});

function blocked(
  blockers: readonly FunnelCalculationReceiptBuildBlocker[],
): FunnelCalculationReceiptBuildResult {
  return {
    contractVersion: FUNNEL_CALCULATION_RECEIPT_BUILDER_CONTRACT_VERSION,
    status: "blocked",
    receipt: null,
    blockers,
  };
}

function used(
  contractVersion: string,
  receiptHash: string,
): FunnelCalculationComponentReference {
  return { status: "used", contractVersion, receiptHash };
}

/**
 * Builds a funnel calculation receipt from upstream contract verdicts.
 *
 * Refusals are collected across all three components rather than short
 * circuiting, so a caller sees every upstream that disqualified the estimate in
 * one pass. A built receipt is a planning-estimate provenance record only: it
 * authorizes no pricing, CRM, activation, or publication action.
 */
export function buildFunnelCalculationReceipt(
  input: FunnelCalculationReceiptBuildInput,
): FunnelCalculationReceiptBuildResult {
  const upstreamBlockers: FunnelCalculationReceiptBuildOnlyBlocker[] = [];
  const { seasonalDemand, priceEvidence, packageRecommendation } = input;

  if (seasonalDemand && seasonalDemand.validation.readiness !== "ready") {
    upstreamBlockers.push("seasonal-demand-component-not-ready");
  }
  if (priceEvidence && priceEvidence.validation.readiness !== "ready") {
    upstreamBlockers.push("price-evidence-component-not-ready");
  }
  // `manualQuoteRequired` is the recommender's own verdict on incomplete
  // pricing information, so it is read directly rather than re-derived here.
  if (packageRecommendation?.recommendation.manualQuoteRequired) {
    upstreamBlockers.push("package-recommendation-component-requires-manual-quote");
  }

  if (upstreamBlockers.length > 0) return blocked(upstreamBlockers);

  const receipt: FunnelCalculationReceipt = {
    classification: FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION,
    funnelStateVersion: input.funnelStateVersion,
    decimalRoundingContractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
    outputScale: input.outputScale,
    calculatorVersion: input.calculatorVersion ?? FUNNEL_CALCULATION_METHOD_VERSION,
    formulaVersion: input.formulaVersion ?? FUNNEL_CALCULATION_FORMULA_VERSION,
    calculationTimestamp: input.calculationTimestamp,
    evidenceDate: input.evidenceDate,
    inputsHash: input.inputsHash,
    outputsHash: input.outputsHash,
    result: { resultHash: input.result.resultHash, unit: input.result.unit },
    components: {
      seasonalDemand: seasonalDemand
        ? used(seasonalDemand.validation.contractVersion, seasonalDemand.receiptHash)
        : NOT_USED,
      priceEvidence: priceEvidence
        ? used(priceEvidence.validation.contractVersion, priceEvidence.receiptHash)
        : NOT_USED,
      packageRecommendation: packageRecommendation
        ? used(
            packageRecommendation.recommendation.ruleVersion,
            packageRecommendation.receiptHash,
          )
        : NOT_USED,
    },
  };

  const validation = validateFunnelCalculationReceipt({ receipt, asOfDate: input.asOfDate });
  if (validation.readiness !== "ready") return blocked(validation.blockers);

  return {
    contractVersion: FUNNEL_CALCULATION_RECEIPT_BUILDER_CONTRACT_VERSION,
    status: "built",
    receipt,
    blockers: [],
  };
}
